import { z } from 'zod/v4';

import { buttonLegacyDefaultDataSchema } from '@sites/entity-core';

import { buttonAnimationSchema } from './button-animation.schema';
import { normalizeButtonWidgetArgs } from './button-widget-args.normalization';
import { partialButtonItemSchema } from './partial-button-item.schema';

/**
 * `buttons` is overridden to hold PARTIAL button items (unlike the real entity schema, where
 * every item must be a complete `ButtonItem` — `buttonBase` alone is a required 6-field object).
 * Asking the model for a complete item in one shot reintroduces the "one big nested object"
 * failure mode this connector has repeatedly hit — a model that only wants to set `label` should
 * be able to send just `{ label: "hey" }`; the caller fills every other field's default via
 * `createButtonItem()`.
 */
const createButtonWidgetDataSchema = buttonLegacyDefaultDataSchema.partial().extend({
    /**
     * Re-declared rather than inherited. `.partial()` above is SHALLOW: it makes `animation`
     * optional and leaves its value schema whole, so `{ animation: { isActive: true } }` was
     * rejected for the three keys it did not carry, and only a complete four-field object got
     * through. The entity has carried the group all along (`animationPluginShapeLegacy` in
     * `buttonLegacyDefaultDataSchema`), so this is the difference between "accepted in theory" and
     * "reachable" — the same partial the text and image widgets take.
     */
    animation: buttonAnimationSchema.optional(),
    buttons: z
        .array(partialButtonItemSchema)
        .optional()
        .describe(
            'The buttons in this group — this single widget can hold one or several buttons rendered ' +
                'together. Each item is a PARTIAL button entity; anything omitted falls back to a sane default ' +
                '(e.g. just { "label": "hey" } is a complete, valid button).'
        ),
});

const createButtonWidgetObjectSchema = z.object({
    columnId: z.string().describe('Id of the column to create the button widget in.'),
    index: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe("Position within the column's widget list (0-based). Omit to append at the end."),
    data: createButtonWidgetDataSchema.describe(
        'Button group data — the buttons array plus shared alignment/indent settings. Anything omitted falls ' +
            'back to a sane default.'
    ),
});

/**
 * Despite the tool description spelling out the real nested shape (with an example), models have
 * repeatedly been observed ignoring it anyway — sending a stray top-level `widgetId` (doesn't
 * exist on create), and `data` as a flat `{ text: "Hey" }` / `{ label: "Hey" }` or even a bare
 * top-level `label` instead of `{ buttons: [{ label: "Hey" }] }`. A plain `z.object()` silently
 * strips unrecognized keys with NO issue raised, so the call would otherwise succeed with an
 * untouched DEFAULT button ("Visit"), silently ignoring the text the user actually asked for.
 * Scaffolding (JSON-string data, malformed-data rejection, synonym cleanup) is shared with
 * `update_button_widget` — see `normalizeButtonWidgetArgs`.
 */
function normalizeCreateButtonWidgetArgs(raw: unknown): unknown {
    return normalizeButtonWidgetArgs(raw, (args, data) => {
        if (!('buttons' in data)) {
            const flatLabel =
                data['label'] ??
                data['text'] ??
                data['buttonText'] ??
                args['label'] ??
                args['text'] ??
                args['buttonText'];
            if (typeof flatLabel === 'string') {
                delete data['label'];
                delete data['text'];
                delete data['buttonText'];
                data['buttons'] = [{ label: flatLabel }];
            }
        }

        // `widgetId` is `update_button_widget`'s lookup key, not a field of this create tool —
        // drop it rather than let it round-trip as confusing noise if the model invents one anyway.
        delete args['widgetId'];

        return data;
    });
}

export const createButtonWidgetArgsSchema = z.preprocess(
    normalizeCreateButtonWidgetArgs,
    createButtonWidgetObjectSchema
);
